import React, { useEffect, useMemo, useState } from "react";
import TopNav from "../../components/Navigation/TopNav";
import MainCard from "@alliancesafetycouncil/asc-card";
import PageTitle from "@alliancesafetycouncil/asc-page-title";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import { useDispatch, useSelector } from "react-redux"
import { listAPIRequest } from "../../actions/API/API"
import { listResourceRequest } from "../../actions/Resource/Resource";
import { useParams } from "react-router-dom";
import {
  resolveOpenApiSpecFromResource,
  isLikelyYamlUrl,
  unwrapSpecDocument,
} from "../../lib/openApiSpec";

const swaggerUiCommon = {
  docExpansion: "list",
  defaultModelsExpandDepth: -1,
  displayRequestDuration: true,
};

const APIDetails = ({ enabled = true }) => {
  const [project, setProject] = useState(null)
  const dispatch = useDispatch()
  const { uuid } = useParams()

  const api = useSelector(state => state.apiReducer.data.find(api => api.uuid === uuid))
  const resources = useSelector(state => state.resourceReducer.data)

  const specSource = useMemo(() => resolveOpenApiSpecFromResource(project), [project])

  const [fetchedSpec, setFetchedSpec] = useState(null)
  const [fetchError, setFetchError] = useState(null)
  const [useUrlFallback, setUseUrlFallback] = useState(false)

  useEffect(() => {
    dispatch(listAPIRequest())
    dispatch(listResourceRequest())
  }, [dispatch])

  useEffect(() => {
    if (uuid && resources && resources.length > 0) {
      const details = resources.find(r => r.api?.uuid === uuid)
      if (details) {
        setProject(details)
      } else {
        if (api) {
          const detailsByName = resources.find(r => r.name === api.name)
          if (detailsByName) {
            setProject(detailsByName)
          }
        }
      }
    }
  }, [uuid, api, resources])

  useEffect(() => {
    setFetchedSpec(null)
    setFetchError(null)
    setUseUrlFallback(false)

    if (!specSource || specSource.kind === "inline") {
      return
    }

    const url = specSource.url

    if (isLikelyYamlUrl(url)) {
      setUseUrlFallback(true)
      return
    }

    let cancelled = false
    fetch(url, { credentials: "omit" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then((text) => {
        if (cancelled) return
        let parsed
        try {
          parsed = JSON.parse(text)
        } catch {
          setUseUrlFallback(true)
          return
        }
        const doc = unwrapSpecDocument(parsed)
        if (doc) {
          setFetchedSpec(doc)
        } else {
          setFetchError(
            "This URL does not return a valid OpenAPI document (no top-level openapi or swagger version). " +
              "Use openApiUrl in the resource data to point at the raw specification JSON or YAML, not an API base URL or HTML page."
          )
        }
      })
      .catch(() => {
        if (cancelled) return
        setUseUrlFallback(true)
      })

    return () => {
      cancelled = true
    }
  }, [specSource])

  if (!enabled) {
    return null
  }

  const apiName = api?.name || 'API';

  const renderSwagger = () => {
    if (!specSource) {
      return (
        <div className="p-8 text-center">
          <i className="fa-light fa-file-circle-question text-6xl text-gray-300 mb-4"></i>
          <h2 className="text-xl font-medium text-gray-600 mb-2">No Documentation Available</h2>
          <p className="text-gray-500">
            OpenAPI documentation has not been configured for this API yet.
          </p>
        </div>
      )
    }

    if (specSource.kind === "inline") {
      return (
        <div className="swagger-ui-wrapper">
          <SwaggerUI
            spec={specSource.spec}
            {...swaggerUiCommon}
          />
        </div>
      )
    }

    const url = specSource.url

    if (fetchError) {
      return (
        <div className="p-8 text-center">
          <i className="fa-light fa-triangle-exclamation text-6xl text-amber-400 mb-4"></i>
          <h2 className="text-xl font-medium text-gray-700 mb-2">Unable to load API documentation</h2>
          <p className="text-gray-600 max-w-xl mx-auto">{fetchError}</p>
        </div>
      )
    }

    if (fetchedSpec) {
      return (
        <div className="swagger-ui-wrapper">
          <SwaggerUI
            key={url}
            spec={fetchedSpec}
            {...swaggerUiCommon}
          />
        </div>
      )
    }

    if (useUrlFallback) {
      return (
        <div className="swagger-ui-wrapper">
          <SwaggerUI
            key={url}
            url={url}
            {...swaggerUiCommon}
          />
        </div>
      )
    }

    return (
      <div className="p-8 text-center text-gray-500">
        <i className="fa-light fa-circle-notch fa-spin text-3xl mb-4"></i>
        <p>Loading documentation…</p>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="fixed w-full z-20">
        <TopNav />
      </div>
      <div className="spacer-for-nav h-17"></div>
      <div className="p-2 md:p-4 h-full">
        <div className="mb-4">
          <PageTitle>
            <i className="fa-light fa-book-open text-orange-500 mr-2"></i>
            {apiName} Documentation
          </PageTitle>
        </div>
        <MainCard type="main">
          <div className="h-full">
            <div className="relative flex flex-col">
              {renderSwagger()}
            </div>
          </div>
        </MainCard>
      </div>
    </div>
  );
};

export default APIDetails;
